import { HttpEventType } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Observable } from 'rxjs';
import { SnackbarTypes } from 'src/app/core/_enums/snackbar.types';
import { UploadFileName } from 'src/app/core/_enums/upload.file.name';
import { Project } from 'src/app/core/_models/project';
import { ProjectType } from 'src/app/core/_models/projectType';
import { Version } from 'src/app/core/_models/version';
import { SnackbarService } from 'src/app/core/_services/snackbar.service';
import { UploadService } from 'src/app/core/_services/upload.service';
import { environment } from 'src/environments/environment';
import { ProjectDataService } from '../../project-data.service';

@Component({
  selector: 'app-project-step-project',
  templateUrl: './project-step-project.component.html',
  styleUrls: ['./project-step-project.component.scss']
})
export class ProjectStepProjectComponent implements OnInit {
  @Input() projectForm!: FormGroup;
  @Input() isFromCallForTenders = false;
  @Input() projectTypes: ProjectType[] = [];
  @Input() databases: Version[] = [];
  @Input() filteredContractors!: Observable<{ id: string; displayName: string }[]>;
  @Input() projectId: string | null = null;
  @Input() projectStatuses: { value: string; labelKey: string }[] = [];
  @Input() currentProject: Project | null = null;

  @Output() onSubmit = new EventEmitter<void>();
  @Output() onSave = new EventEmitter<void>();
  @Output() onDelete = new EventEmitter<void>();
  @Output() onNextStep = new EventEmitter<void>();
  @Output() onPreviousStep = new EventEmitter<void>();

  energies$: Observable<any[]>;
  isLoading$: Observable<boolean>;
  wsAppPath = environment.baseUrl;
  logos: { field: UploadFileName; property: 'projectLogo' | 'customerLogo' | 'ueLogo'; labelKey: string }[] = [
    { field: UploadFileName.logoProjet, property: 'projectLogo', labelKey: 'project_logo_contractor' },
    { field: UploadFileName.logoCustomer, property: 'customerLogo', labelKey: 'project_logo_customer' },
    { field: UploadFileName.logoUo, property: 'ueLogo', labelKey: 'project_logo_company' }
  ];

  constructor(
    private projectDataService: ProjectDataService,
    private uploadService: UploadService,
    private snackbarService: SnackbarService
  ) {
    this.energies$ = this.projectDataService.getEnergies();
    this.isLoading$ = this.projectDataService.loading$;
  }

  ngOnInit(): void {
    this.projectDataService.ensureDataLoaded().subscribe();
  }

  get formCrls() {
    return this.projectForm.controls;
  }

  compareProjectTypes(pt1: ProjectType, pt2: ProjectType): boolean {
    return pt1 && pt2 ? pt1.projectTypeId === pt2.projectTypeId : pt1 === pt2;
  }

  displayContractorName(contractor: { id: string | null; displayName: string } | string): string {
    if (typeof contractor === 'string') {
      return contractor;
    }
    return contractor?.displayName || '';
  }

  getMoaLength(): number {
    const value = this.projectForm.value.projectContractorObject;
    if (!value) return 0;
    if (typeof value === 'string') return value.length;
    return value.displayName?.length || 0;
  }

  handleSubmit() {
    this.onSubmit.emit();
  }

  handleSave() {
    this.onSave.emit();
  }

  handleDelete() {
    this.onDelete.emit();
  }

  handleNextStep() {
    this.onNextStep.emit();
  }

  handlePreviousStep() {
    this.onPreviousStep.emit();
  }

  uploadLogo(logo: { field: UploadFileName; property: 'projectLogo' | 'customerLogo' | 'ueLogo' }, files: FileList | null): void {
    if (!this.projectId) return;
    if (!files || files.length === 0) return;

    const fileToUpload = files[0];
    const formData = new FormData();
    formData.append('file', fileToUpload, fileToUpload.name);

    this.uploadService.uploadLogoProject(this.projectId, logo.field, formData).subscribe(
      (event: any) => {
        if (event.type !== HttpEventType.Response) return;
        if (!this.currentProject) return;
        this.currentProject[logo.property] = `Documents/ProjectImages/${this.projectId}/${fileToUpload.name}`;
      },
      () => {
        this.snackbarService.open(SnackbarTypes.errorUploadLogo);
      }
    );
  }

  deleteLogo(property: 'projectLogo' | 'customerLogo' | 'ueLogo'): void {
    if (!this.currentProject) return;
    this.currentProject[property] = undefined;
  }

  getLogoFileName(property: 'projectLogo' | 'customerLogo' | 'ueLogo'): string {
    const path = this.getLogoRelativePath(property);
    if (!path) return '';
    const segments = path.split('/');
    return segments[segments.length - 1] || '';
  }

  getLogoUrl(property: 'projectLogo' | 'customerLogo' | 'ueLogo'): string {
    const path = this.getLogoRelativePath(property);
    return path ? this.wsAppPath + path : '';
  }

  private getLogoRelativePath(property: 'projectLogo' | 'customerLogo' | 'ueLogo'): string {
    const raw = this.currentProject?.[property];
    if (!raw) return '';
    const normalized = raw.replace(/\\/g, '/');
    const docIndex = normalized.indexOf('Documents/');
    return docIndex >= 0 ? normalized.substring(docIndex) : normalized;
  }
}
